import { Suspense, lazy } from "react";
import type { ComponentType, ReactNode } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import { homeRoute, isClientUser, isRegionalUser, useAuth } from "@/app/AuthContext";
import { LandingPage } from "@/pages/public/LandingPage";
import {
  AboutPage,
  AppealPage,
  ContactPage,
  MapPage,
  MobileAppPage,
  ServicesPage,
  SubsystemsPage,
  TrackingPage,
} from "@/pages/public/SectionPages";
import {
  MarketPage as PublicMarketPage,
  PrivacyPage,
  SeedlingsPage,
  TermsPage,
} from "@/pages/public/ModulePages";
import { MarketProductPage } from "@/pages/public/MarketProductPage";
import { AuctionLotsPage } from "@/pages/public/AuctionLotsPage";
import { AuctionLotPage } from "@/pages/public/AuctionLotPage";
import { PublicLayout } from "@/shared/layouts/PublicLayout";
import { FAQ_HREF, GUIDES_HREF, MEDIA_HREF, SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { LoginPage } from "@/pages/auth/LoginPage";
import { OneIdCallbackPage, OneIdMobileCallbackPage } from "@/pages/auth/OneIdCallbackPage";
import { NewsArticlePage, NewsListPage } from "@/pages/public/NewsPages";
import { ServiceDetailPage } from "@/pages/public/ServiceDetailPage";
import { OneIdSandboxPage, ServiceGatewayPage } from "@/pages/public/ServiceGatewayPage";
import { DecisionVerifyPage } from "@/pages/public/DecisionVerifyPage";
import { PublicPage } from "@/pages/public/PublicPage";
import { useI18n } from "@/shared/i18n";
import { CabinetProvider, CabinetShell } from "@/shared/layouts/CabinetLayout";

/*
 * Kabinet sahifalari kechiktirilgan yuklanadi (ЧТЗ 4.1.1 — dastlabki yuklama
 * hajmi nazorati): publik portal va kirish sahifasi ochilganda modul kodlari
 * yuklanmaydi, har bir bo'lim birinchi ochilganda alohida chunk sifatida keladi.
 */
const DashboardPage = lazy(() => page(import("@/pages/cabinet/DashboardPage"), "DashboardPage"));
const EventsPage = lazy(() => page(import("@/pages/cabinet/EventsPage"), "EventsPage"));
const AppealsPage = lazy(() => page(import("@/pages/cabinet/AppealsPage"), "AppealsPage"));
const MonitoringPage = lazy(() => page(import("@/pages/cabinet/MonitoringPage"), "MonitoringPage"));
const MapsPage = lazy(() => page(import("@/pages/cabinet/MapsPage"), "MapsPage"));
const MapViewPage = lazy(() => page(import("@/pages/cabinet/MapViewPage"), "MapViewPage"));
const MapCombinedPage = lazy(() =>
  page(import("@/pages/cabinet/MapCombinedPage"), "MapCombinedPage"),
);
const AfforestationPage = lazy(() =>
  page(import("@/pages/cabinet/AfforestationPage"), "AfforestationPage"),
);
const AfforestationPlansPage = lazy(() =>
  page(import("@/pages/cabinet/AfforestationPlansPage"), "AfforestationPlansPage"),
);
/* Ekologiya — 3 sahifa: bioxilma-xillik, uglerod balansi, ekologik analitika. */
const BiodiversityPage = lazy(() =>
  page(import("@/pages/cabinet/ecology/BiodiversityPage"), "BiodiversityPage"),
);
const CarbonPage = lazy(() => page(import("@/pages/cabinet/ecology/CarbonPage"), "CarbonPage"));
const EcologyAnalyticsPage = lazy(() =>
  page(import("@/pages/cabinet/ecology/AnalyticsPage"), "AnalyticsPage"),
);
/* Ijara moduli — reestr, arizalar, monitoring va nazorat alohida chunklarda. */
const LeaseRegistryPage = lazy(() =>
  page(import("@/pages/cabinet/lease/LeaseRegistryPage"), "LeaseRegistryPage"),
);
const LeaseApplicationsPage = lazy(() =>
  page(import("@/pages/cabinet/lease/LeaseApplicationsPage"), "LeaseApplicationsPage"),
);
const LeaseApplicationCardPage = lazy(() =>
  page(import("@/pages/cabinet/lease/LeaseApplicationCardPage"), "LeaseApplicationCardPage"),
);
const LeaseApplicationFormPage = lazy(() =>
  page(import("@/pages/cabinet/lease/LeaseApplicationFormPage"), "LeaseApplicationFormPage"),
);
const LeaseStatusMapPage = lazy(() =>
  page(import("@/pages/cabinet/lease/LeaseStatusMapPage"), "LeaseStatusMapPage"),
);
const AuctionPage = lazy(() => page(import("@/pages/cabinet/lease/AuctionPage"), "AuctionPage"));
const LeaseMonitoringPage = lazy(() =>
  page(import("@/pages/cabinet/lease/LeaseMonitoringPage"), "LeaseMonitoringPage"),
);
const LeaseViolationsPage = lazy(() =>
  page(import("@/pages/cabinet/lease/LeaseViolationsPage"), "LeaseViolationsPage"),
);
const BillingPage = lazy(() => page(import("@/pages/cabinet/BillingPage"), "BillingPage"));
const ProductsPage = lazy(() => page(import("@/pages/cabinet/ProductsPage"), "ProductsPage"));
const MarketPage = lazy(() => page(import("@/pages/cabinet/MarketPage"), "MarketPage"));
const CorporatePage = lazy(() => page(import("@/pages/cabinet/CorporatePage"), "CorporatePage"));
const IntegrationsPage = lazy(() =>
  page(import("@/pages/cabinet/IntegrationsPage"), "IntegrationsPage"),
);
const ReportsPage = lazy(() => page(import("@/pages/cabinet/ReportsPage"), "ReportsPage"));
const UsersPage = lazy(() => page(import("@/pages/cabinet/UsersPage"), "UsersPage"));
const OversightPage = lazy(() => page(import("@/pages/cabinet/OversightPage"), "OversightPage"));
const NotificationsPage = lazy(() =>
  page(import("@/pages/cabinet/NotificationsPage"), "NotificationsPage"),
);
const ProfilePage = lazy(() => page(import("@/pages/cabinet/ProfilePage"), "ProfilePage"));
const DashboardsViewPage = lazy(() =>
  page(import("@/pages/cabinet/DashboardsViewPage"), "DashboardsViewPage"),
);

/* Superadmin paneli — alohida chunk: uni faqat huquqi bor foydalanuvchi ochadi. */
const AdminHomePage = lazy(() => page(import("@/pages/admin/AdminHomePage"), "AdminHomePage"));
const ModelListPage = lazy(() => page(import("@/pages/admin/ModelListPage"), "ModelListPage"));
const ModelFormPage = lazy(() => page(import("@/pages/admin/ModelFormPage"), "ModelFormPage"));
const SqlConsolePage = lazy(() => page(import("@/pages/admin/SqlConsolePage"), "SqlConsolePage"));
const GeoServicesPage = lazy(() => page(import("@/pages/admin/GeoServicesPage"), "GeoServicesPage"));
/* Konstruktorlar (ЧТЗ 4.2.7, Modul 7.2) — dasturlashsiz konfiguratsiya. */
const AttributesPage = lazy(() =>
  page(import("@/pages/admin/constructors/AttributesPage"), "AttributesPage"),
);
const DictionariesPage = lazy(() =>
  page(import("@/pages/admin/constructors/DictionariesPage"), "DictionariesPage"),
);
const StylesPage = lazy(() => page(import("@/pages/admin/constructors/StylesPage"), "StylesPage"));
const WorkflowBuilderPage = lazy(() =>
  page(import("@/pages/admin/constructors/WorkflowBuilderPage"), "WorkflowBuilderPage"),
);
const ReportBuilderPage = lazy(() =>
  page(import("@/pages/admin/constructors/ReportBuilderPage"), "ReportBuilderPage"),
);
const DashboardBuilderPage = lazy(() =>
  page(import("@/pages/admin/constructors/DashboardBuilderPage"), "DashboardBuilderPage"),
);
const MenuManagerPage = lazy(() =>
  page(import("@/pages/admin/MenuManagerPage"), "MenuManagerPage"),
);

/* Mijoz kabineti — tashqi foydalanuvchilar (ijarachi, xaridor) uchun. */
const ClientHomePage = lazy(() => page(import("@/pages/client/ClientHomePage"), "ClientHomePage"));
const ClientServicesPage = lazy(() =>
  page(import("@/pages/client/ClientServicesPage"), "ClientServicesPage"),
);
const ClientAppealsPage = lazy(() =>
  page(import("@/pages/client/ClientAppealsPage"), "ClientAppealsPage"),
);
const ClientBillingPage = lazy(() =>
  page(import("@/pages/client/ClientBillingPage"), "ClientBillingPage"),
);
const ClientPlotsPage = lazy(() => page(import("@/pages/client/ClientPlotsPage"), "ClientPlotsPage"));
const ClientMarketPage = lazy(() =>
  page(import("@/pages/client/ClientMarketPage"), "ClientMarketPage"),
);
const ClientProfilePage = lazy(() =>
  page(import("@/pages/client/ClientProfilePage"), "ClientProfilePage"),
);

/* Hududiy boshqarma kabineti — viloyat xodimlari uchun. */
const RegionalLoginPage = lazy(() =>
  page(import("@/pages/regional/RegionalLoginPage"), "RegionalLoginPage"),
);
const RegionalLotsPage = lazy(() =>
  page(import("@/pages/regional/RegionalLotsPage"), "RegionalLotsPage"),
);
const RegionalMyPlotsPage = lazy(() =>
  page(import("@/pages/regional/RegionalMyPlotsPage"), "RegionalMyPlotsPage"),
);
const RegionalOrdersPage = lazy(() =>
  page(import("@/pages/regional/RegionalOrdersPage"), "RegionalOrdersPage"),
);
const PaymentSandboxPage = lazy(() =>
  page(import("@/pages/public/PaymentSandboxPage"), "PaymentSandboxPage"),
);
const RegionalHomePage = lazy(() =>
  page(import("@/pages/regional/RegionalHomePage"), "RegionalHomePage"),
);
const RegionalProductsPage = lazy(() =>
  page(import("@/pages/regional/RegionalProductsPage"), "RegionalProductsPage"),
);
const RegionalLeasePage = lazy(() =>
  page(import("@/pages/regional/RegionalLeasePage"), "RegionalLeasePage"),
);
const RegionalBillingPage = lazy(() =>
  page(import("@/pages/regional/RegionalBillingPage"), "RegionalBillingPage"),
);
const RegionalApplicationsPage = lazy(() =>
  page(import("@/pages/regional/RegionalLeasePage"), "RegionalApplicationsPage"),
);
const RegionalContractsPage = lazy(() =>
  page(import("@/pages/regional/RegionalBillingPage"), "RegionalContractsPage"),
);

/* Elektron do'kon — alohida sahifa (o'z sidebar/sarlavhasi), alohida chunk. */

/** Nomlangan eksportni `React.lazy` kutadigan `default` shakliga o'giradi. */
async function page<T extends Record<string, unknown>>(
  loader: Promise<T>,
  name: keyof T,
): Promise<{ default: ComponentType }> {
  let module: T;
  try {
    module = await loader;
  } catch (error) {
    /* Deploydan keyin ochiq turgan eski varaq yangi bo'lmagan chunk'ni so'raydi: bir marta sahifani
       yangilaymiz (aks holda sahifa bo'sh qolib ketadi). Takroriy aylanish sessionStorage bilan to'xtatiladi. */
    try {
      if (!sessionStorage.getItem("chunk-reload")) {
        sessionStorage.setItem("chunk-reload", "1");
        window.location.reload();
        return new Promise<never>(() => undefined);
      }
    } catch {
      /* sessionStorage yopiq bo'lsa — xatoni o'zi ko'rsatamiz */
    }
    throw error;
  }
  try {
    sessionStorage.removeItem("chunk-reload");
  } catch {
    /* ahamiyatsiz */
  }
  return { default: module[name] as ComponentType };
}

/**
 * Kabinet sahifalari himoyalangan. Backend umuman javob bermayotgan konturda
 * (masalan faqat frontend ishga tushirilgan) sahifa demo ma'lumot bilan
 * ochiladi — aks holda dizaynni ko'rib bo'lmaydi.
 */
function RequireAuth({
  children,
  area = "cabinet",
}: {
  children: ReactNode;
  /** Qaysi kontur: xodimlar kabineti yoki mijoz kabineti. */
  area?: "cabinet" | "client" | "regional";
}) {
  const { status, offline, user } = useAuth();
  const { t } = useI18n();

  if (status === "checking") {
    return <Loading text={t("state.loading")} />;
  }
  if (status === "anonymous" && !offline) {
    return <Navigate to={area === "regional" ? "/hudud/kirish" : "/kirish"} replace />;
  }
  /* Konturlar aralashmasligi kerak: mijoz xodimlar kabinetiga tushib qolsa —
     bo'sh sahifalar va «huquq yetarli emas» xatolarini ko'radi. */
  if (user) {
    const client = isClientUser(user);
    if (area === "cabinet" && client) return <Navigate to="/mijoz" replace />;
    /* Hududiy xodim «/kabinet» ildizida emas, o'z kabinetida ishlaydi; ariza kartochkasi kabi
       ichki sahifalar esa (ijara bosqichlari, ERI) kabinetdan ochiladi. */
    if (area === "cabinet" && isRegionalUser(user) && window.location.pathname === "/kabinet") {
      return <Navigate to="/hudud" replace />;
    }
    if (area === "client" && !client && !user.permissions?.includes("client.portal")) {
      return <Navigate to="/kabinet" replace />;
    }
    if (area === "regional" && !user.permissions?.includes("regional.portal")) {
      return <Navigate to={homeRoute(user)} replace />;
    }
  }
  return <>{children}</>;
}

/**
 * `/kirish`: sessiya allaqachon (sahifa ochilganda) tiklangan bo'lsa — login
 * so'ralmaydi, foydalanuvchi o'z kabinetiga avtomatik o'tadi.
 */
function LoginRoute() {
  const { status, user, restored } = useAuth();
  const next = new URLSearchParams(window.location.search).get("next") ?? "";
  const safe = next.startsWith("/") && !next.startsWith("//") && !next.includes("\\") ? next : "";
  if (status === "checking") return <Loading text="" />;
  if (status === "authenticated" && restored) {
    return <Navigate to={safe || homeRoute(user)} replace />;
  }
  return <LoginPage />;
}

/** Matn markazda, uchta nuqta ketma-ket "yonib" turadi (statik "…" o'rniga). */
function LoadingText({ text }: { text: string }) {
  return (
    <p className="muted loading-dots">
      {text.replace(/[.…]+$/, "")}
      <span className="loading-dots__dot" />
      <span className="loading-dots__dot" />
      <span className="loading-dots__dot" />
    </p>
  );
}

function Loading({ text }: { text: string }) {
  return (
    <div className="loading-state" style={{ minHeight: "100vh" }}>
      <LoadingText text={text} />
    </div>
  );
}

/**
 * Kabinet ichidagi Suspense fallback — sidebar/topbar `CabinetShell`da doimiy
 * turgani uchun bu faqat kontent maydonini egallaydi (100vh emas), aks holda
 * bo'lim almashganda butun karkas bir lahzaga g'oyib bo'lib "tebranadi".
 */
function PageLoading({ text }: { text: string }) {
  return (
    <div className="content loading-state" style={{ minHeight: "50vh" }}>
      <LoadingText text={text} />
    </div>
  );
}

/**
 * Kabinet marshrutlari — yon menyudagi har bir bo'lim o'z sahifasini ochadi.
 * Marshrut nomlari `shared/data/navigation.ts` dagi `to` bilan bir xil.
 *
 * Barchasi bitta «layout route» ichida: jonli hisoblagichlar provayderi
 * bo'limlar almashganda qayta o'rnatilmaydi.
 */
const CABINET_ROUTES: Array<{ path: string; element: ReactNode }> = [
  { path: "/kabinet", element: <DashboardPage /> },
  { path: "/kabinet/hodisalar", element: <EventsPage /> },
  { path: "/kabinet/murojaatlar", element: <AppealsPage /> },
  { path: "/kabinet/kuzatuv", element: <MonitoringPage /> },
  { path: "/kabinet/xaritalar", element: <MapsPage /> },
  { path: "/kabinet/xaritalar/umumiy", element: <MapCombinedPage /> },
  { path: "/kabinet/xaritalar/:code", element: <MapViewPage /> },
  /* Eski manzillar — yo'nalishlar ro'yxatiga yo'naltiriladi. */
  { path: "/kabinet/xarita", element: <Navigate to="/kabinet/xaritalar" replace /> },
  { path: "/kabinet/qatlamlar", element: <Navigate to="/kabinet/xaritalar" replace /> },
  { path: "/kabinet/tiklash", element: <AfforestationPage /> },
  { path: "/kabinet/tiklash/rejalar", element: <AfforestationPlansPage /> },
  { path: "/kabinet/ekologiya", element: <Navigate to="/kabinet/ekologiya/bioxilma-xillik" replace /> },
  { path: "/kabinet/ekologiya/bioxilma-xillik", element: <BiodiversityPage /> },
  { path: "/kabinet/ekologiya/uglerod", element: <CarbonPage /> },
  { path: "/kabinet/ekologiya/analitika", element: <EcologyAnalyticsPage /> },
  { path: "/kabinet/ijara", element: <LeaseRegistryPage /> },
  { path: "/kabinet/ijara/arizalar", element: <LeaseApplicationsPage /> },
  { path: "/kabinet/ijara/arizalar/yangi", element: <LeaseApplicationFormPage /> },
  { path: "/kabinet/ijara/arizalar/:id", element: <LeaseApplicationCardPage /> },
  { path: "/kabinet/ijara/arizalar/:id/tahrirlash", element: <LeaseApplicationFormPage /> },
  { path: "/kabinet/ijara/xarita", element: <LeaseStatusMapPage /> },
  /* Eski bildirishnomalardagi havolalar. */
  { path: "/kabinet/ijara/jarayon/:id", element: <LeaseApplicationCardPage /> },
  { path: "/kabinet/ijara/eauksion", element: <Navigate to="/kabinet/ijara/e-auksion" replace /> },
  { path: "/kabinet/ijara/e-auksion", element: <AuctionPage /> },
  { path: "/kabinet/ijara/monitoring", element: <LeaseMonitoringPage /> },
  { path: "/kabinet/ijara/nazorat", element: <LeaseViolationsPage /> },
  { path: "/kabinet/billing", element: <BillingPage /> },
  { path: "/kabinet/mahsulotlar", element: <ProductsPage /> },
  { path: "/kabinet/bozor", element: <MarketPage /> },
  { path: "/kabinet/korporativ", element: <CorporatePage /> },
  { path: "/kabinet/integratsiyalar", element: <IntegrationsPage /> },
  { path: "/kabinet/hisobotlar", element: <ReportsPage /> },
  { path: "/kabinet/dashboardlar", element: <DashboardsViewPage /> },
  { path: "/kabinet/foydalanuvchilar", element: <UsersPage /> },
  { path: "/kabinet/nazorat", element: <OversightPage /> },
  { path: "/kabinet/bildirishnomalar", element: <NotificationsPage /> },
  { path: "/kabinet/profil", element: <ProfilePage /> },
];

/**
 * Superadmin paneli — kabinetdan alohida marshrut fazosi va alohida karkas
 * (`AdminShell`). Kabinet menyusi va jonli hisoblagichlar provayderi bu yerda
 * ishlamaydi: xizmat konturi operativ kabinet bilan aralashmasligi kerak.
 */
const ADMIN_ROUTES: Array<{ path: string; element: ReactNode }> = [
  { path: "/superadmin", element: <AdminHomePage /> },
  { path: "/superadmin/sql", element: <SqlConsolePage /> },
  { path: "/superadmin/geoxizmatlar", element: <GeoServicesPage /> },
  /* Konstruktorlar — 6 blok */
  { path: "/superadmin/atributlar", element: <AttributesPage /> },
  { path: "/superadmin/spravochniklar", element: <DictionariesPage /> },
  { path: "/superadmin/uslublar", element: <StylesPage /> },
  { path: "/superadmin/workflow", element: <WorkflowBuilderPage /> },
  { path: "/superadmin/hisobotlar", element: <ReportBuilderPage /> },
  { path: "/superadmin/dashboard", element: <DashboardBuilderPage /> },
  { path: "/superadmin/menyu", element: <MenuManagerPage /> },
  { path: "/superadmin/jadval/:key", element: <ModelListPage /> },
  { path: "/superadmin/jadval/:key/yangi", element: <ModelFormPage /> },
  { path: "/superadmin/jadval/:key/:pk", element: <ModelFormPage /> },
];

/**
 * Mijoz kabineti — uchinchi kontur: xodimlar kabineti ham, superadmin paneli
 * ham bu yerda ko'rinmaydi. Bo'limlar foydalanuvchi huquqiga qarab ochiladi.
 */
const CLIENT_ROUTES: Array<{ path: string; element: ReactNode }> = [
  { path: "/mijoz", element: <ClientHomePage /> },
  { path: "/mijoz/xizmatlar", element: <ClientServicesPage /> },
  { path: "/mijoz/murojaatlar", element: <ClientAppealsPage /> },
  { path: "/mijoz/hisob", element: <ClientBillingPage /> },
  { path: "/mijoz/ijara", element: <ClientPlotsPage /> },
  { path: "/mijoz/bozor", element: <ClientMarketPage /> },
  { path: "/mijoz/profil", element: <ClientProfilePage /> },
];

/** Hududiy boshqarma kabineti — to'rtinchi kontur (`/hudud`). */
const REGIONAL_ROUTES: Array<{ path: string; element: ReactNode }> = [
  { path: "/hudud", element: <RegionalHomePage /> },
  { path: "/hudud/uchastkalarim", element: <RegionalMyPlotsPage /> },
  { path: "/hudud/mahsulotlar", element: <RegionalProductsPage /> },
  { path: "/hudud/buyurtmalar", element: <RegionalOrdersPage /> },
  { path: "/hudud/lotlar", element: <RegionalLotsPage /> },
  { path: "/hudud/ijara", element: <RegionalLeasePage /> },
  { path: "/hudud/arizalar", element: <RegionalApplicationsPage /> },
  { path: "/hudud/shartnomalar", element: <RegionalContractsPage /> },
  { path: "/hudud/tolovlar", element: <RegionalBillingPage /> },
];

export function App() {
  const { t } = useI18n();

  return (
    <Routes>
      {/* Publik bo'limlar: sarlavha va futer bitta layout route'da — o'zgarmaydi. */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<LandingPage />} />
        <Route path={SECTION_PATHS.about} element={<AboutPage />} />
        <Route path={SECTION_PATHS.news} element={<NewsListPage />} />
        <Route path={`${SECTION_PATHS.news}/:id`} element={<NewsArticlePage />} />
        <Route path={SECTION_PATHS.subsystems} element={<SubsystemsPage />} />
        <Route path={SECTION_PATHS.services} element={<ServicesPage />} />
        <Route path={`${SECTION_PATHS.services}/:code`} element={<ServiceDetailPage />} />
        <Route path={SECTION_PATHS.appeal} element={<AppealPage />} />
        <Route path={SECTION_PATHS.tracking} element={<TrackingPage />} />
        {/* Avval yuborilgan SMS'lardagi eski havola (`/track`). */}
        <Route path="/track" element={<TrackingPage />} />
        <Route path={SECTION_PATHS.map} element={<MapPage />} />
        <Route path={SECTION_PATHS.guides} element={<Navigate to={GUIDES_HREF} replace />} />
        <Route path={SECTION_PATHS.media} element={<Navigate to={MEDIA_HREF} replace />} />
        <Route path={SECTION_PATHS["mobile-app"]} element={<MobileAppPage />} />
        <Route path={SECTION_PATHS.faq} element={<Navigate to={FAQ_HREF} replace />} />
        <Route path={SECTION_PATHS.contact} element={<ContactPage />} />
        <Route path={SECTION_PATHS.lease} element={<AuctionLotsPage />} />
        <Route path={`${SECTION_PATHS.lease}/:lotNumber`} element={<AuctionLotPage />} />
        <Route path={SECTION_PATHS.market} element={<PublicMarketPage />} />
        <Route path={`${SECTION_PATHS.market}/:code`} element={<MarketProductPage />} />
        <Route path={SECTION_PATHS.seedlings} element={<SeedlingsPage />} />
        <Route path={SECTION_PATHS.terms} element={<TermsPage />} />
        <Route path={SECTION_PATHS.privacy} element={<PrivacyPage />} />
      </Route>
      <Route path="/kirish" element={<LoginRoute />} />
      <Route path="/auth/oneid/callback" element={<OneIdCallbackPage />} />
      <Route path="/auth/oneid/mobile/callback" element={<OneIdMobileCallbackPage />} />
      <Route path="/auth/oneid/sandbox" element={<OneIdSandboxPage />} />
      {/* ЧТЗ 4.2.11.5 — xizmat shlyuzi: OneID'dan keyin shu yerga qaytiladi. */}
      <Route path="/xizmat/:code" element={<ServiceGatewayPage />} />
      {/* Portal menyusidagi «Ichki sahifa» bandlari (CMS sahifalari). */}
      <Route path="/pages/:slug" element={<PublicPage />} />
      <Route path="/tekshiruv/qaror/:id" element={<DecisionVerifyPage />} />

      <Route
        element={
          <RequireAuth>
            <CabinetProvider>
              <CabinetShell>
                <Suspense fallback={<PageLoading text={t("state.loading")} />}>
                  <Outlet />
                </Suspense>
              </CabinetShell>
            </CabinetProvider>
          </RequireAuth>
        }
      >
        {CABINET_ROUTES.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
      </Route>

      <Route
        element={
          <RequireAuth>
            <Suspense fallback={<Loading text={t("state.loading")} />}>
              <Outlet />
            </Suspense>
          </RequireAuth>
        }
      >
        {ADMIN_ROUTES.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
      </Route>

      <Route
        element={
          <RequireAuth area="client">
            <Suspense fallback={<Loading text={t("state.loading")} />}>
              <Outlet />
            </Suspense>
          </RequireAuth>
        }
      >
        {CLIENT_ROUTES.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
      </Route>

      <Route path="/hudud/kirish" element={<RegionalLoginPage />} />
      <Route path="/payments/sandbox" element={<PaymentSandboxPage />} />

      <Route
        element={
          <RequireAuth area="regional">
            <Suspense fallback={<Loading text={t("state.loading")} />}>
              <Outlet />
            </Suspense>
          </RequireAuth>
        }
      >
        {REGIONAL_ROUTES.map((route) => (
          <Route key={route.path} path={route.path} element={route.element} />
        ))}
      </Route>

      {/* Panel kabinet ichida bo'lgan davrdagi eski manzillar. */}
      <Route path="/kabinet/superadmin/*" element={<Navigate to="/superadmin" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
